import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useAuthStore from '@store/useAuthStore';
import { createPlayerApi, selectPlayerApi, updateUserPropertiesApi } from '@/api/userApi';
import { GAME_MODES } from '@utils/constants';
import { getBrandConfig } from '@utils/brandHelper';
import { usePageBackground } from '@/hooks/usePageBackground';
import MainMenu from '@components/ui/game/main-menu/MainMenu';
import { useT } from '@/i18n';
import './Onboarding.css';

const brandConfig = getBrandConfig();

export default function Onboarding() {
  const navigate = useNavigate();
  const t = useT();
  const [playerName, setPlayerName] = useState('');
  const [selectedMode, setSelectedMode] = useState('simple_test');
  const [error, setError] = useState(null);

  // Check if user already has players
  const players = useAuthStore((state) => state.players);
  const activePlayer = useAuthStore((state) => state.activePlayer);
  const user = useAuthStore((state) => state.user);
  const hasPlayers = players && players.length > 0;

  // Set onboarding background via Asset system
  usePageBackground('bg.onboarding');

  // Pre-fill player name from existing player or user name
  useEffect(() => {
    const defaultName = activePlayer?.name || players?.[0]?.name || user?.name || '';
    if (defaultName && !playerName) {
      setPlayerName(defaultName);
    }
  }, [activePlayer, players, user]);

  const { addPlayer, setActivePlayer } = useAuthStore.getState();

  // Mutation for creating player
  const createPlayerMutation = useMutation({
    mutationFn: (name) => createPlayerApi(name),
  });

  // Mutation for selecting player
  const selectPlayerMutation = useMutation({
    mutationFn: (playerId) => selectPlayerApi(playerId),
  });

  // Mutation for updating user properties
  const updatePropertiesMutation = useMutation({
    mutationFn: (properties) => updateUserPropertiesApi(properties),
  });

  const isLoading = createPlayerMutation.isPending ||
    selectPlayerMutation.isPending ||
    updatePropertiesMutation.isPending;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Only validate player name if user doesn't have players
    if (!hasPlayers && !playerName.trim()) {
      setError(t('onboarding.error_name_required'));
      return;
    }

    try {
      // If user doesn't have players, create one first
      if (!hasPlayers) {
        // 1. Create player
        const createResponse = await createPlayerMutation.mutateAsync(playerName);
        const newPlayer = createResponse.data.player;

        // 2. Add to store and set as active
        addPlayer(newPlayer, true);

        // 3. Select player in backend
        await selectPlayerMutation.mutateAsync(newPlayer.id);
        setActivePlayer(newPlayer.id);
      }

      // Save preferred game mode to user properties
      await updatePropertiesMutation.mutateAsync({
        preferred_game_mode: selectedMode,
      });

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.userMessage || t('onboarding.error_generic'));
    }
  };

  return (
    <div className="main-container main-container--onboarding">
      <MainMenu />

      <div className="onboarding-content section">
        <h1 className="onboarding-title">
          {hasPlayers ? t('onboarding.select_mode') : t('onboarding.welcome', { name: brandConfig.name })}
        </h1>
        <p className="onboarding-subtitle">
          {hasPlayers ? t('onboarding.choose_adventure') : t('onboarding.setup_profile')}
        </p>

        <form onSubmit={handleSubmit} className="onboarding-form">
          {/* Player Name Section - Only show if no players */}
          {!hasPlayers && (
            <div className="onboarding-section onboarding-section--name">
              <label className="onboarding-label">{t('onboarding.player_name')}</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder={t('onboarding.enter_name')}
                className="onboarding-input"
                maxLength={255}
                required
                data-sfx-hover="input"
              />
            </div>
          )}

          {/* Game Mode Section */}
          <div className="onboarding-section onboarding-section--modes">
            <label className="onboarding-label">{t('onboarding.game_mode')}</label>
            <div className="onboarding-modes">
              {GAME_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`onboarding-mode-card ${selectedMode === mode.id ? 'selected' : ''} ${!mode.available ? 'disabled' : ''}`}
                  onClick={() => mode.available && setSelectedMode(mode.id)}
                  disabled={!mode.available}
                  data-sfx="button"
                >
                  {mode.image && (
                    <div className="mode-image-wrapper">
                      <img src={mode.image} alt={t(mode.nameKey)} className="mode-image" />
                    </div>
                  )}
                  <span className="mode-name">{t(mode.nameKey)}</span>
                  <span className="mode-description">{t(mode.descriptionKey)}</span>
                  {!mode.available && <span className="mode-badge">{t('onboarding.coming_soon')}</span>}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="error-message">{error}</p>}

          <button
            type="submit"
            className="button button-cta"
            disabled={isLoading || (!hasPlayers && !playerName.trim())}
            data-sfx="button"
          >
            {isLoading ? t('onboarding.setting_up') : t('onboarding.start_playing')}
          </button>
        </form>
      </div>
    </div>
  );
}
